import { AxiosError, AxiosResponse } from 'axios';

import { Logger } from '../../common/Logger';

/**
 * Error response format variants found in the codebase:
 *
 * 1. Legacy PHP format:
 *    { success: false, msg: "error message" }
 *    { success: false, error: "error message" }
 *    { meta: { success: false, error_message: "error message" } }
 *
 * 2. REST API format:
 *    { success: false, result: {}, msg: "error message" }
 *    { error: "error message" }
 *
 * 3. GraphQL format:
 *    { errors: [{ message: "error message" }] }
 *
 * 4. Validation errors:
 *    { errors: { field: ["error message"] } }
 */

export interface ErrorMessageOptions {
  /** Default message if no custom message found */
  defaultMessage?: string;
  /** Context for the operation (used in logging) */
  context?: string;
  /** Log the error to CloudWatch/console */
  logError?: boolean;
}

/**
 * Extracts a user-friendly error message from various API response formats.
 *
 * This utility handles multiple response formats used across the Kixie API:
 * - Legacy PHP responses (success: false, msg: "...")
 * - REST API responses (error: "...")
 * - GraphQL errors
 * - Validation errors
 * - Axios errors
 *
 * @param response - Axios response or error object
 * @param options - Configuration options
 * @returns User-friendly error message
 *
 * @example
 * // Basic usage
 * const errorMsg = extractErrorMessage(response);
 *
 * @example
 * // With custom default message
 * const errorMsg = extractErrorMessage(response, {
 *   defaultMessage: 'Failed to save contact'
 * });
 *
 * @example
 * // With context and logging
 * const errorMsg = extractErrorMessage(response, {
 *   defaultMessage: 'Failed to replace number',
 *   context: 'ReplaceNumberModal',
 *   logError: true
 * });
 */
export function extractErrorMessage(
  response: AxiosResponse | AxiosError | any,
  options: ErrorMessageOptions = {}
): string {
  const {
    defaultMessage = 'An unexpected error occurred. Please try again.',
    context = 'Unknown',
    logError = true,
  } = options;

  let errorMessage = defaultMessage;
  const responseData = response?.data || response?.response?.data;

  try {
    // 1. Check for Legacy PHP format: { success: false, msg: "..." }
    if (responseData?.msg && typeof responseData.msg === 'string') {
      errorMessage = responseData.msg;
    }
    // 2. Check for Legacy PHP format: { success: false, error: "..." }
    else if (responseData?.error && typeof responseData.error === 'string') {
      errorMessage = responseData.error;
    }
    // 3. Check for message field: { success: false, message: "..." }
    else if (
      responseData?.message &&
      typeof responseData.message === 'string'
    ) {
      errorMessage = responseData.message;
    }
    // 4. Check for PHP meta format: { meta: { success: false, error_message: "..." } }
    else if (responseData?.meta?.error_message) {
      errorMessage = responseData.meta.error_message;
    }
    // 5. Check for GraphQL errors: { errors: [{ message: "..." }] }
    else if (
      Array.isArray(responseData?.errors) &&
      responseData.errors[0]?.message
    ) {
      errorMessage = responseData.errors[0].message;
    }
    // 6. Check for validation errors: { errors: { field: ["message"] } }
    else if (responseData?.errors && typeof responseData.errors === 'object') {
      const firstFieldErrors = Object.values(responseData.errors)[0];
      if (Array.isArray(firstFieldErrors) && firstFieldErrors[0]) {
        errorMessage = firstFieldErrors[0] as string;
      }
    }
    // 7. Check for Axios error message
    else if (response?.message && typeof response.message === 'string') {
      // Only use axios message if it's not too technical
      if (
        !response.message.includes('Network Error') &&
        !response.message.includes('timeout') &&
        !response.message.includes('CORS')
      ) {
        errorMessage = response.message;
      }
    }
    // 8. Check for HTTP status text
    else if (response?.statusText && response.statusText !== 'Unknown') {
      errorMessage = `${defaultMessage} (${response.statusText})`;
    }

    // Log error if enabled
    if (logError) {
      Logger.error(
        {
          context,
          extractedMessage: errorMessage,
          defaultMessage,
          responseData,
          status: response?.status || response?.response?.status,
        },
        'extractErrorMessage'
      );
    }
  } catch (error) {
    // Fallback to default if extraction fails
    Logger.error(
      {
        context,
        message: 'Failed to extract error message',
        error,
        response,
      },
      'extractErrorMessage'
    );
  }

  return errorMessage;
}
